/**
 * Is a logo pale and one-coloured (a cream or white wordmark, made for dark backgrounds)? Only such a logo is drawn
 * darkened on a light page — a full-colour logo (Hey Kiddo's badge) darkened becomes a black blot. Same rule as the
 * API's PDF renderer (engine/logoTone.ts): the visible pixels' mean lightness and how many of them carry colour.
 */
export function paleFromPixels(data: Uint8ClampedArray | Uint8Array): boolean | null {
  let n = 0; let lum = 0; let colourful = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 40) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    n++; lum += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (Math.max(r, g, b) - Math.min(r, g, b) > 50) colourful++;
  }
  if (!n) return null;
  return lum / n > 0.75 && colourful / n < 0.1;
}

const verdicts = new Map<string, Promise<boolean | null>>();
/** Read the logo's pixels in the browser (a separate CORS load, so the shown image never depends on it); null when it cannot be read. */
export function logoIsPale(src: string): Promise<boolean | null> {
  if (!verdicts.has(src)) verdicts.set(src, new Promise((resolve) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const s = 48 / Math.max(img.naturalWidth || 48, img.naturalHeight || 48);
        const c = document.createElement('canvas'); c.width = Math.max(1, Math.round((img.naturalWidth || 48) * s)); c.height = Math.max(1, Math.round((img.naturalHeight || 48) * s));
        const ctx = c.getContext('2d'); if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, c.width, c.height);
        resolve(paleFromPixels(ctx.getImageData(0, 0, c.width, c.height).data));
      } catch { resolve(null); } // a host without CORS: no verdict
    };
    img.onerror = () => resolve(null);
    img.src = src;
  }));
  return verdicts.get(src)!;
}
